/**
 * Store capabilities — the **delegation** gate (`12-chain.md` § 2).
 *
 * This is a third axis, above roles and separate from entitlements. HQ grants
 * or withholds a substore's right to configure its own marketing and top-ups,
 * so a store manager may hold `marketing:write` and still be unable to act,
 * because HQ has not delegated the capability to that store.
 *
 * The three gates run in sequence and answer different questions:
 *
 *   entitlement  requireFeature          did the merchant buy this?     402
 *   delegation   requireStoreCapability  did HQ allow this store to?    403
 *   identity     requirePermission       may this user do it?           403
 *
 * Like the other two catalogs, this one is checked bidirectionally against the
 * `requireStoreCapability(...)` literals in the API: a typo'd capability is a
 * gate that denies everyone forever with a correct-looking 403, and a catalog
 * key nothing reads is a switch in HQ's UI that does nothing.
 */

type CapabilityDefinition = {
  readonly key: string;
  readonly name: string;
  readonly description: string;
  /** What a single-site merchant gets. Delegation only bites in a chain. */
  readonly defaultGranted: boolean;
};

const DEFINITIONS = [
  {
    key: "configure_coupons",
    name: "Configure coupons",
    description: "This store may create and edit its own coupons.",
    defaultGranted: true,
  },
  {
    key: "configure_discount_promos",
    name: "Configure discount promos",
    description: "This store may create and edit its own spend-and-save promotions.",
    defaultGranted: true,
  },
  {
    key: "configure_topup_tiers",
    name: "Configure top-up tiers",
    description: "This store may set its own stored-value bonus tiers.",
    defaultGranted: true,
  },
  {
    key: "custom_topup_amounts",
    name: "Custom top-up amounts",
    description:
      "Staff at this store may key in a top-up amount rather than choosing a preset tier.",
    /**
     * Granted, like the other three.
     *
     * It was `false`, which read as a strict default and was in fact a dead
     * end: `defaultGranted` is "what a single-site merchant gets", and the
     * only screen that can change it sits behind `requireFeature("CHAIN_STORE")`.
     * A merchant with no head office could never have keyed an odd top-up
     * amount, with nowhere to turn it back on. Delegation is meant to bite in
     * a chain, and now it only does.
     */
    defaultGranted: true,
  },
] as const satisfies readonly CapabilityDefinition[];

export type StoreCapability = (typeof DEFINITIONS)[number]["key"];

export const STORE_CAPABILITY_CATALOG: readonly CapabilityDefinition[] = DEFINITIONS;

export const STORE_CAPABILITIES = DEFINITIONS.map((c) => c.key) as unknown as readonly [
  StoreCapability,
  ...StoreCapability[],
];

const BY_KEY = new Map<string, CapabilityDefinition>(DEFINITIONS.map((c) => [c.key, c]));

export function isStoreCapability(value: string): value is StoreCapability {
  return BY_KEY.has(value);
}

export function storeCapability(key: StoreCapability): CapabilityDefinition {
  const found = BY_KEY.get(key);
  if (!found) throw new Error(`Unknown store capability: ${key}`);
  return found;
}

/** What a store holds before HQ has said anything. */
export function defaultCapabilities(): Record<StoreCapability, boolean> {
  const out = {} as Record<StoreCapability, boolean>;
  for (const definition of DEFINITIONS) out[definition.key] = definition.defaultGranted;
  return out;
}

/**
 * Capabilities that are grantable before any route checks them. Same
 * shrink-only contract as the feature and permission reserves: an entry leaves
 * this list the moment a route starts enforcing it.
 *
 * Empty, and that is the point. Head office could tick and untick all four,
 * the values persisted, the back office drew real checkboxes — and nothing in
 * the product read the answer, which made the whole delegation gate a
 * merchant-facing control that silently did nothing.
 *
 * `custom_topup_amounts` was the last to leave, and it needed a decision
 * rather than a route: it defaulted to denied while `defaultGranted` means
 * "what a single-site merchant gets", and the only screen that could grant it
 * is behind `requireFeature("CHAIN_STORE")`. It now defaults to granted, so it
 * bites a branch head office has withheld it from and nobody else.
 */
export const RESERVED_CAPABILITIES: readonly StoreCapability[] = [];
